import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, Subject } from 'rxjs';
import { AbstractCRUD, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import { Attachment, Indication, QuickLink } from '../../shared/models';
import { CONFIG } from '../../config';
import { catchError } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class IndicationService extends AbstractCRUD<Indication> implements CRUDService<Indication> {

  private openNewNoteEditorSource = new Subject<void>();
  public openNewNoteEditor$ = this.openNewNoteEditorSource.asObservable();
  private goToOverviewNoteSource = new Subject<void>();
  public goToOverviewNote$ = this.goToOverviewNoteSource.asObservable();
  private quickLinksModifiedSource = new Subject<void>();
  public quickLinksModified$ = this.quickLinksModifiedSource.asObservable();

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService
  ) {
    super(exceptionService, http, utilsService);
  }

  public getResourcePath(): string {
    return '/rms/indications';
  }

  /**
   * get and submit a request to create an object
   * @param  {boolean} isClassification
   * @param  {Indication} objToProcess
   * @returns Observable
   */
  public createObj(isClassification: boolean, objToProcess: Indication): Observable<any> {
    if (isClassification) {
      return this.createClassification(objToProcess);
    } else {
      return this.createIndication(objToProcess);
    }
  }

  /**
   * get and submit a request to edit an object
   * @param  {boolean} isClassification
   * @param  {Indication} objToProcess
   * @returns Observable
   */
  public editObj(isClassification: boolean, objToProcess: Indication): Observable<any> {
    if (isClassification) {
      return this.updateClassification(objToProcess, objToProcess.id);
    } else {
      return this.updateIndication(objToProcess, objToProcess.id);
    }
  }

  /**
   * Creates a classification (called from the create classification modal)
   * @param  formData
   */
  public createClassification (formData: any) {
    return super.getHTTP()
      .post(`/api/rms/classifications`, formData)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Creates an indication (called from the create classification modal)
   * @param  formData
   */
  public createIndication (formData: any) {
    return super.getHTTP()
      .post(`/api/rms/indications`, formData)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Retrieves all classifications in the system
   * @returns Observable
   */
  public getClassifications(): Observable<PaginatedList<Indication>> {
    return super.getHTTP()
    .get(`/api/rms/classifications?pagesize=${CONFIG.pagination.singleLargePage}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Get an indication by its Id
   * @param  {number} id
   * @returns Observable
   */
  public getIndicationById(id: number): Observable<Indication> {
    return super.getHTTP()
      .get(`/api/rms/indications/${id}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Get all indications
   * @returns Observable
   */
  public getIndications(): Observable<PaginatedList<Indication>> {
    return super.getHTTP()
      .get(`/api/rms/indications`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Retrieves all 'experts' (or users) that are experts of the specified indications
   * @returns Observable
   */
  public getIndicationsExperts(): Observable<PaginatedList<Indication>> {
    return super.getHTTP()
    .get(`/api/rms/indications/experts?pagesize=${CONFIG.pagination.singleLargePage}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Updates an existing classification
   * @param  formData
   * @param  {number} classificationId
   */
  public updateClassification (formData: any, classificationId: number) {
    return super.getHTTP()
      .put(`/api/rms/classifications/${classificationId}`, formData)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Updates an existing indication
   * @param formData
   * @param  {number} indicationId
   */
  public updateIndication (formData: any, indicationId: number) {
    return super.getHTTP()
      .put(`/api/rms/indications/${indicationId}`, formData)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * searching of Indication
   * @param body
   */
  public searchIndication (body: any) {
    return super.getHTTP()
      .post(`/api/rms/indications/search?page=${body.page}&pagesize=${body.pagesize}&excludePartial=true`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Searching for an classifications and experts
   * @param entity
   * @param params
   */
  public autocompleteIndications<T>(entity: string, params: any): Observable<PaginatedList<T>> {
    return super.getHTTP()
    .get(`/api/rms/${entity}?q=${params.q}`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Returns the page of the most recent Overview Note
   * @param indicationId
   * @param params
   */
  public getMostRecentOverviewNote(indicationId: number, params?: any): Observable<any> {
    return super.getHTTP().get(`/api/rms/indications/${indicationId}/notes/overview`, params);
  }

  /**
   * Gets the list of quick links
   * @param indicationId
   * @param params
   */
  public getQuickLinks(indicationId: number, params?: any): Observable<PaginatedList<QuickLink>> {
    return super.getHTTP()
      .get(`/api/rms/indications/${indicationId}/links`, params)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Creates a new quick link
   * @param indicationId
   * @param body
   */
  public createQuickLink(indicationId: number, body: Partial<QuickLink>): Observable<QuickLink> {
    return super.getHTTP()
      .post(`/api/rms/indications/${indicationId}/links/`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Updates a quick link
   * @param indicationId
   * @param linkId
   * @param body
   */
  public updateQuickLink(indicationId: number, linkId: number, body: Partial<QuickLink>): Observable<QuickLink> {
    return super.getHTTP()
      .put(`/api/rms/indications/${indicationId}/links/${linkId}`, body)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Deletes a quick link
   * @param indicationId
   * @param linkId
   */
  public deleteQuickLink(indicationId: number, linkId: number): Observable<void> {
    return super.getHTTP()
      .delete(`/api/rms/indications/${indicationId}/links/${linkId}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Get indications key papers
   * @param indicationId
   * @param pageSize
   */
  public getKeyPapers(indicationId: number, pageSize: number): Observable<PaginatedList<Attachment>> {
    return super.getHTTP()
      .get(`/api/rms/indications/${indicationId}/keypapers?pagesize=${pageSize}&sort=created&direction=DESC`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  /**
   * Triggers Opening a new Note Editor
   */
  public openNewNoteEditor(): void {
    this.openNewNoteEditorSource.next();
  }

  /**
   * Triggers Going to the most recent overview note
   */
  public goToOverviewNote(): void {
    this.goToOverviewNoteSource.next();
  }

  /**
   * Triggers when the links section in the edit indication modal form is updated to reload the quick links in the header
   */
  public quickLinksModified(): void {
    this.quickLinksModifiedSource.next();
  }
}
